import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, signal, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatChipsModule } from "@angular/material/chips";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatCardModule } from "@angular/material/card";
import { MatDialogModule } from "@angular/material/dialog";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";

import { SearchDisplayMode, SearchPanelConfig, SearchPanelSummary, SearchState, SearchType } from "../../model";
import { SearchQueryService } from "../../services/search";
import { SearchUtilsService } from "../../services/search/utils/search-utils.service";
import { CustomElementsLauncherService } from "../../services/custom-elements-launcher.service";
import { FeatureFlagsService } from "../../../viewer-components/services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../../viewer-components/model/feature-flags";

/**
 * Reusable search panel component that displays search summary with export/save actions
 * Used by both grid-display-search and search-enhanced-results components
 */
@Component({
  selector: "app-search-summary-panel",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./search-summary-panel.component.html",
  styleUrls: ["./search-summary-panel.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatCardModule,
    MatDialogModule,
    MatProgressSpinnerModule,
  ],
})
export class SearchSummaryPanelComponent implements OnInit, OnDestroy {
  public readonly SearchUtilsService = SearchUtilsService;

  protected searchQueryService = inject(SearchQueryService);

  private customElementsLauncher = inject(CustomElementsLauncherService);

  private cdr = inject(ChangeDetectorRef);

  private overlay = inject(Overlay);

  private viewContainerRef = inject(ViewContainerRef);

  private overlayRef: OverlayRef | null = null;

  @ViewChild("imageOverlayTemplate") imageOverlayTemplate: TemplateRef<unknown>;

  protected featureFlagsService = inject(FeatureFlagsService);

  /** Whether the map is currently in 3D mode — Custom Elements detection is not supported there */
  protected readonly is3D = signal(false);

  /** Set from the `is3DMode` input (forwarded from the map container) so the detection
   * button can react to 2D/3D switches without waiting on `MapService.mapStatus$`. */
  @Input() set is3DMode(value: boolean) {
    this.is3D.set(!!value);
  }

  _searchState: SearchState;

  /** Current search state to display */
  @Input() set searchState(state: SearchState | null) {
    this._searchState = state;
    this.generatePanelSummary();
  }

  get searchState(): SearchState | null {
    return this._searchState;
  }

  /** Configuration for panel display */
  @Input() config: SearchPanelConfig = {
    showExportButton: true,
    showSaveButton: true,
    customClasses: [],
  };

  /** Panel summary information */
  panelSummary: SearchPanelSummary | null = null;

  /** Selected image for display dialog */
  selectedImageSrc: string | null = null;

  SearchType = SearchType;

  ngOnInit(): void {
    this.generatePanelSummary();
  }

  /**
   * Generate the panel summary information based on current search state
   */
  private generatePanelSummary(): void {
    if (!this.searchState) {
      this.panelSummary = null;
      this.cdr.detectChanges();
      return;
    }

    const currentQuery = this.searchQueryService.getCurrentSearchQuery();

    if (!currentQuery) {
      this.panelSummary = null;
      this.cdr.detectChanges();
      return;
    }
    switch (currentQuery.searchType) {
      case SearchType.TEXT:
        this.panelSummary = {
          searchType: SearchType.TEXT,
          title: "Text Search",
          description: `Search query: "${this.searchState.query}"`,
          preview: this.searchState.query,
          icon: "search",
        };
        break;

      case SearchType.IMAGE_URL:
        this.panelSummary = {
          searchType: SearchType.IMAGE_URL,
          title: "Image URL Search",
          description: "Search based on image from URL",
          preview: currentQuery.imageUrl,
          icon: "link",
        };
        break;

      case SearchType.IMAGE_DATA:
        this.panelSummary = {
          searchType: SearchType.IMAGE_DATA,
          title: "Uploaded Image Search",
          description: "Search based on uploaded image",
          preview: currentQuery.imagePreview ?? currentQuery.imageBase64,
          icon: "file_upload",
        };
        break;

      case SearchType.LOCATION:
        if (currentQuery.location) {
          this.panelSummary = {
            searchType: SearchType.LOCATION,
            title: "Location Search",
            description: `Coordinates: ${currentQuery.location[0].toFixed(6)}, ${currentQuery.location[1].toFixed(6)}`,
            preview: currentQuery.location.join(", "),
            icon: "place",
            metadata: {
              coordinates: currentQuery.location,
              radius: currentQuery.radiusMeters,
            },
          };
        } else if (currentQuery.wkt) {
          this.panelSummary = {
            searchType: SearchType.LOCATION,
            title: "Geometry Search",
            description: `WKT Geometry: ${this.truncateText(currentQuery.wkt, 50)}`,
            preview: currentQuery.wkt,
            icon: "polyline",
          };
        }
        break;

      default:
        this.panelSummary = {
          searchType: SearchType.TEXT,
          title: "Search Results",
          description: this.searchState.query,
          preview: this.searchState.query,
          icon: "search",
        };
    }

    this.cdr.detectChanges();
  }

  /**
   * Truncate text for display purposes
   */
  private truncateText(text: string, maxLength: number): string {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  }

  /**
   * Handle export action
   */
  onExport(): void {
    this.searchQueryService.requestExport();
  }

  onLoadMore(): void {
    this.searchQueryService.loadMoreResults().subscribe();
  }

  onClearSearch(): void {
    // For now, this just emits the event
    this.searchQueryService.clearSearch();
  }

  /**
   * Bridge into Custom Elements Detection — builds a MULTIPOLYGON from every
   * result geometry and hands it off to the launcher service. The
   * CustomElementsToolbarComponent picks it up and jumps to the configure stage.
   *
   * Each result's geometry may be a POLYGON or a MULTIPOLYGON (e.g. when a
   * search result was imported from a multi-polygon file). Both are flattened
   * into the same MULTIPOLYGON so every polygon becomes a detection target.
   */
  onDetectObjects(): void {
    const results = this.searchState?.results ?? [];
    if (!results.length) return;

    const polygonBlocks: string[] = [];
    const areas = results.flatMap((item) => {
      const blocks = SearchSummaryPanelComponent.extractPolygonBlocks(item.geometry);
      polygonBlocks.push(...blocks);
      return blocks.map((block) => ({
        wkt: `POLYGON${block}`,
        collection: item.collection ?? undefined,
      }));
    });
    if (!areas.length) return;

    this.customElementsLauncher.launch({
      areas,
      visualWkt: `MULTIPOLYGON(${polygonBlocks.join(",")})`,
      label: "Detection area based on previous search results",
    });
  }

  /** Returns full `POLYGON((...))` WKT strings from a POLYGON or MULTIPOLYGON. */
  private static extractPolygonWkts(wkt: string | undefined): string[] {
    return SearchSummaryPanelComponent.extractPolygonBlocks(wkt).map((block) => `POLYGON${block}`);
  }

  /**
   * Extracts the child polygon blocks (each already wrapped as `((...))`) from
   * a POLYGON or MULTIPOLYGON WKT, ready to be concatenated inside
   * `MULTIPOLYGON(...)`. Returns an empty array for unsupported / falsy input.
   */
  private static extractPolygonBlocks(wkt: string | undefined): string[] {
    if (!wkt) return [];

    const polygonMatch = wkt.match(/^\s*POLYGON\s*\(([\s\S]*)\)\s*$/i);
    if (polygonMatch) {
      return [`(${polygonMatch[1]})`];
    }

    const multiMatch = wkt.match(/^\s*MULTIPOLYGON\s*\(([\s\S]*)\)\s*$/i);
    if (multiMatch) {
      return SearchSummaryPanelComponent.splitTopLevelBlocks(multiMatch[1]);
    }

    return [];
  }

  /**
   * Splits a MULTIPOLYGON body string into its top-level polygon blocks,
   * walking parentheses depth so that holes (inner rings) stay attached to
   * their parent polygon. Each returned chunk is one full `((ring) [, (ring)])`
   * polygon.
   */
  private static splitTopLevelBlocks(body: string): string[] {
    const blocks: string[] = [];
    let depth = 0;
    let start = -1;
    for (let i = 0; i < body.length; i += 1) {
      const c = body[i];
      if (c === "(") {
        if (depth === 0) start = i;
        depth += 1;
      } else if (c === ")") {
        depth -= 1;
        if (depth === 0 && start >= 0) {
          blocks.push(body.substring(start, i + 1));
          start = -1;
        }
      }
    }
    return blocks;
  }

  /**
   * Open image in dialog for better viewing
   */
  openImageDialog(imageSrc: string): void {
    this.selectedImageSrc = imageSrc;
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: "cdk-overlay-dark-backdrop",
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
    const portal = new TemplatePortal(this.imageOverlayTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.overlayRef.backdropClick().subscribe(() => this.closeImageDialog());
  }

  /**
   * Close image dialog
   */
  closeImageDialog(): void {
    this.selectedImageSrc = null;
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy(): void {
    this.closeImageDialog();
  }

  /**
   * Check if current search type is image-based
   */
  get isImageSearch(): boolean {
    return (
      this.panelSummary?.searchType === SearchType.IMAGE_URL || this.panelSummary?.searchType === SearchType.IMAGE_DATA
    );
  }

  /**
   * Get tooltip text for image preview
   */
  get imageTooltip(): string {
    if (this.panelSummary?.searchType === SearchType.IMAGE_URL) {
      return "Click to view image in full size";
    }
    if (this.panelSummary?.searchType === SearchType.IMAGE_DATA) {
      return "Click to view uploaded image";
    }
    return "";
  }

  /**
   * Check if there's a valid image source to display
   */
  get hasValidImageSrc(): boolean {
    const preview = this.panelSummary?.preview;
    if (!preview) return false;

    // For image uploads, check if it's a valid base64 data URL
    if (this.panelSummary?.searchType === SearchType.IMAGE_DATA) {
      return preview.startsWith("data:image/");
    }

    // For image URLs, basic URL validation
    if (this.panelSummary?.searchType === SearchType.IMAGE_URL) {
      try {
        new URL(preview);
        return true;
      } catch {
        return false;
      }
    }

    return false;
  }

    protected readonly FeatureFlags = FeatureFlags;
    protected readonly SearchDisplayMode = SearchDisplayMode;
}
